import { observer } from 'mobx-react-lite';
import { FC, useState, useMemo } from 'react';
import { Story } from '../../types.ts/story';
import styled from 'styled-components';

import { ProduceIcon } from '../common/icons';
import SocialClipShareModal from './SocialClipShareModal';
import VideoClipCard from './VideoClipCard';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import AIClipProducer from '../ClipProducer/AIClipProducer';
import ClipProducerModalUI from './ClipProducer/ClipProducerModalUI';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  useDatoClient,
  useTemplatedPrompts,
  useUserIdentity,
  useTranscription,
} from '@src/stores-v2/StoreContext';
import { hasSpeakerData } from '@src/utility/speakerUtils';

type Props = {
  aiGeneratedContent?: Story['aiGeneratedContent'];
};
const SocialClipsView: FC<Props> = observer((props) => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const templatedPrompts = useTemplatedPrompts();
  const transcriptionStore = useTranscription();
  const [showProducerModal, toggleProducerModal] = useState<boolean>(false);
  const clipProducerPresets = templatedPrompts?.clipProducerPresets;
  const { contentStudioEnableClipProducer, enableTranscriptEditor } =
    useFlagsCombination(userIdentity.currentRole);

  // Determine if speaker selection should be enabled based on:
  // 1. The transcript editor feature flag being enabled
  // 2. The presence of speaker data in the transcript
  const enableSpeakerSelection = useMemo(() => {
    if (!enableTranscriptEditor) return false;

    const transcriptElements =
      transcriptionStore.getFinalTranscriptionElements();
    return hasSpeakerData(transcriptElements);
  }, [
    enableTranscriptEditor,
    transcriptionStore.getFinalTranscriptionElements(),
  ]);

  return (
    <Main>
      {contentStudioEnableClipProducer && (
        <ProducerClipButton
          disabled={!clipProducerPresets?.length}
          onClick={() => {
            if (clipProducerPresets?.length) {
              toggleProducerModal(true);
            }
          }}
        >
          <ProduceIcon /> <span>Produce Clips</span>
        </ProducerClipButton>
      )}
      {videoCreator.videoClipPreview && (
        <SocialClipShareModal
          clip={videoCreator.videoClipPreview}
          onClose={() => (videoCreator.videoClipPreview = null)}
        />
      )}
      <Content>
        {videoCreator.story?.otherVideos
          ?.filter(
            (video) =>
              !video.isHidden &&
              (video.videoFilePrimary ||
                video.associatedVideos.find((v) => v.videoFilePrimary) ||
                video.sourcePlatform === 'content-studio'),
          )
          .map((video, index) => (
            <VideoClipCard key={video.id || index} video={video} />
          ))}
      </Content>
      {contentStudioEnableClipProducer && showProducerModal && (
        <AIClipProducer
          useFinalOrOriginalVideo={true}
          supportedPresets={templatedPrompts?.clipProducerPresets || null}
          enableSpeakerSelection={enableSpeakerSelection}
          ClipProducerUI={(props) => (
            <ClipProducerModalUI
              {...props}
              closeModal={() => toggleProducerModal(false)}
            />
          )}
        />
      )}
    </Main>
  );
});

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Content = styled.div`
  margin-top: 10px;
  row-gap: 40px;
  column-gap: 24px;
  place-items: center;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(392px, 1fr));
`;

const EditButton = styled.button.attrs((props: { disabled: boolean }) => props)`
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  border-radius: 8px;
  border: 1px solid #484848;
  background: none;

  opacity: ${(props) => (props.disabled ? '0.5' : '1')};
  cursor: ${(props) => (props.disabled ? 'wait' : 'pointer')};

  color: #f3e9d7;
  font-size: 14px;
  font-weight: 700;
  line-height: normal;
`;

const ProducerClipButton = styled(EditButton)`
  background: #17c964;
  color: #03041a;
  margin-left: auto;
  width: 240px;
`;
export default SocialClipsView;
